import { useState, type FormEvent } from "react"
import { Link, useNavigate } from "react-router-dom"
import { getCase } from "../api"
import { track } from "@/lib/analytics"
import type { FoundDecision } from "../contract"
import { usePopover } from "../ds/usePopover"
import { shortDate } from "./format"
import { daysBefore, downloadIcs, reminderIcs } from "./ics"
import { Overlay } from "./Overlay"
import { useAnswerSession } from "./session"
import "./pro.css"

/* The moments the answer offers more (Flow board lanes A, D and E): every decision found, the
   department's side, and a reminder. Sign-in by number is not built yet, so the steps that need
   a number say so plainly instead of pretending to send. */

/** "11 more decisions found": everything the searches turned up, the ones Nira read first. */
export function AllDecisions({ turn, found, shown }: { turn: string; found: FoundDecision[]; shown: Set<string> }) {
  const [open, setOpen] = useState(false)
  const openSource = useAnswerSession((s) => s.openSource)
  const more = found.filter((f) => !f.source_id || !shown.has(f.source_id))
  if (!more.length) return null
  const id = `alld-${turn}`
  return (
    <div className="pro-all">
      <button type="button" className="pro-link" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        {open ? "Hide the other decisions" : `${more.length} more ${more.length === 1 ? "decision" : "decisions"} found`}
        <span aria-hidden="true">{open ? "↑" : "→"}</span>
      </button>
      {open ? (
        <div id={id}>
          <p className="pro-note">In the order the searches ranked them. Nira read the ones you can open; the rest it found but did not read.</p>
          <ol className="pro-found">
            {more.map((f) => (
              <li key={f.doc_id}>
                {f.source_id ? (
                  <button type="button" className="pro-found-t" onClick={(e) => openSource({ turn, id: f.source_id!, from: e.currentTarget })}>{f.title}</button>
                ) : (
                  <span className="pro-found-t">{f.title}</span>
                )}
                <span className="pro-found-m">{[f.court, f.year, f.outcome.replace(/_/g, " ")].filter(Boolean).join(" · ")}{f.source_id ? " · read" : ""}</span>
                {f.snippet ? <span className="pro-found-s">{f.snippet}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  )
}

/** The department's reading, as it sits on each allegation in Notice analysis (NA-Card): what
 * each row holds, described, never sample text. */
const DEPT_ROWS = [
  { k: "They say", v: "The department's own words, quoted from your notice", locked: false },
  { k: "What it turns on", v: "The records an officer weighs on that charge", locked: false },
  { k: "Likely call · their strongest point · your best answer", v: "The officer's reading of the allegation", locked: true },
]

/**
 * Section 03's card (Results-v5 #dept). The department's reading lives in Notice analysis, card by
 * card on each allegation; this card is the way in. With a notice already in this research, it
 * opens that notice's analysis; otherwise it asks for the notice. What is free and what is Nira for
 * replies is said on the card, so the step to paying is the reader's own.
 */
export function DepartmentCard({ id }: { id: string }) {
  const caseFile = useAnswerSession((s) => s.caseFile)
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  // The notice in this research: its latest reading if it has one, else the step that reads it.
  const openAnalysis = async () => {
    if (!caseFile) return
    setBusy(true)
    const base = `/case/${encodeURIComponent(caseFile.id)}`
    try {
      const detail = await getCase(caseFile.id)
      const latest = [...(detail.readings ?? [])].reverse().find((r) => r.kind === "notice_no_reply")
      navigate(latest ? `${base}/reading/${encodeURIComponent(latest.id)}` : `${base}/confirm`)
    } catch {
      navigate(`${base}/confirm`)
    }
  }
  return (
    <div className="pro-dept" id={id}>
      <div className="pro-dept-h">
        <svg width="22" height="22" viewBox="0 0 56 56" aria-hidden="true">
          <line x1="8" y1="26" x2="48" y2="26" stroke="var(--against)" strokeWidth="5.5" strokeLinecap="round" transform="rotate(10 28 26)" />
          <circle cx="28" cy="36" r="5.5" fill="var(--ink)" />
        </svg>
        <p>How the department would read this</p>
        <span className="pro-tag">Notice analysis</span>
      </div>
      <p className="pro-dept-b">
        {caseFile
          ? "Nira reads your notice allegation by allegation, the way the officer will: their words, what each charge turns on, and where it leans."
          : "On your notice, Nira reads each allegation the way the officer will: their words, what the charge turns on, and where it leans."}
      </p>
      <ul className="pro-dept-rows" aria-label="On each allegation">
        {DEPT_ROWS.map((r) => (
          <li key={r.k} className={r.locked ? "locked" : undefined}>
            <b>{r.k}</b><span>{r.v}{r.locked ? " · Pro, first one free" : " · free"}</span>
          </li>
        ))}
      </ul>
      <div className="pro-dept-a">
        {caseFile ? (
          <button type="button" className="pro-btn" disabled={busy} onClick={() => { track("department_card_clicked", { target: "open_analysis" }); void openAnalysis() }}>
            {busy ? "Opening…" : "Open the notice analysis"}
          </button>
        ) : (
          <Link className="pro-btn" to="/ask?door=notice" onClick={() => track("department_card_clicked", { target: "notice" })}>Analyse your notice</Link>
        )}
        {caseFile ? null : <Link className="pro-link" to="/ask?door=appeal" onClick={() => track("department_card_clicked", { target: "appeal" })}>An order instead? Appeal or not<span aria-hidden="true">→</span></Link>}
      </div>
      <p className="pro-dept-n">Free: the time limit, the money and what each allegation turns on. The officer's reading of each allegation: your first one is free, then it is Pro.</p>
    </div>
  )
}

const OFFSETS = [
  { days: 7, word: "A week before" },
  { days: 5, word: "5 days before" },
  { days: 2, word: "2 days before" },
]

/** "Remind me a week before" beside the reply-by date (Flow lane D). */
export function RemindMe({ date, title }: { date: string; title: string }) {
  const { open, setOpen, button, panel } = usePopover()
  const [days, setDays] = useState(7)
  const choices = OFFSETS.map((o) => ({ ...o, on: daysBefore(date, o.days) })).filter((o) => o.on && o.on >= today())
  if (!choices.length) return null
  const chosen = choices.find((c) => c.days === days) ?? choices[0]
  const calendar = () => {
    const due = shortDate(date)
    downloadIcs(
      `nira-reminder-${chosen.on}.ics`,
      reminderIcs({ on: chosen.on!, title: `Due ${due}: ${title}`, detail: `Nira reminder, ${chosen.word.toLowerCase()} the ${due} date.`, url: window.location.href }),
    )
    setOpen(false)
  }
  return (
    <div className="pro-remind">
      <button type="button" className="pro-link" ref={button} aria-expanded={open} onClick={() => setOpen(!open)}>
        Remind me {chosen.word.toLowerCase()}<span aria-hidden="true">→</span>
      </button>
      {open ? (
        <div className="pro-pop" ref={panel} role="dialog" aria-label="Remind me">
          <fieldset>
            <legend>When</legend>
            {choices.map((c) => (
              <label key={c.days} className="pro-radio">
                <input type="radio" name="remind-when" checked={c.days === chosen.days} onChange={() => setDays(c.days)} />
                <span>{c.word}</span>
                <span className="m">{shortDate(c.on!)}</span>
              </label>
            ))}
          </fieldset>
          <button type="button" className="pro-btn" onClick={calendar}>Add to my calendar</button>
          <button type="button" className="pro-btn ghost" disabled aria-describedby="remind-wa">On WhatsApp</button>
          <p className="pro-soon" id="remind-wa">WhatsApp reminders are coming: a message before the date, and Done or Snooze from the chat.</p>
        </div>
      ) : null}
    </div>
  )
}

const today = () => new Date().toISOString().slice(0, 10)

/**
 * "Your number, once" (Overlay-SignIn board). The form is the design's; verifying a number
 * needs the phone sign-in service, which is not built, so submitting says so and sends nothing.
 */
export function NumberOverlay({ title, why, onClose }: { title: string; why: string; onClose: () => void }) {
  const [said, setSaid] = useState("")
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSaid("Sign-in by number is not switched on yet, so nothing was sent. Add it to your calendar for now.")
  }
  return (
    <Overlay label="Your number, once" onClose={onClose}>
      <p className="nr-ov-k">{title}</p>
      <h2 className="nr-ov-h">Your number, once.</h2>
      <p className="nr-ov-p">{why}</p>
      <form className="nr-ov-f" onSubmit={submit}>
        <label htmlFor="nr-phone" className="nr-ov-lb">Mobile number</label>
        <div className="nr-ov-ph">
          <span className="nr-mono">+91</span>
          <input id="nr-phone" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="98480 00000" pattern="[0-9 ]{10,11}" required />
        </div>
        <button type="submit" className="pro-btn">Continue on WhatsApp</button>
        <button type="submit" className="pro-btn ghost">Send a code by SMS instead</button>
      </form>
      {said ? <p className="nr-ov-said" role="status">{said}</p> : null}
      <p className="nr-ov-foot">No email, no password. We only ever message you about your workspaces.</p>
      <button type="button" className="nr-ov-not" onClick={onClose}>Not now</button>
    </Overlay>
  )
}
